import Component from "../../lib/component";
import {getElementComponentOfType} from "../../lib/global_components";
import {CollapserToggle} from "./collapser_toggle";
import {CollapserTarget} from "./collapser_target";

/**
 * Collapser Component
 *
 * A collapsible content container with smooth CSS height transitions for both
 * opening and closing animations.
 *
 * Architecture:
 * - Collapser: Parent component managing state (isOpen, isCollapsable)
 * - CollapserToggle: Clickable element(s) that trigger open/close
 * - CollapserTarget: The content area that expands/collapses
 *
 * Animation Strategy:
 * The target's height is managed to enable CSS transitions:
 * - When closed: height is locked to 0px
 * - When opening: height transitions from 0px to calculated content height,
 *   then unlocks to 'auto' after animation completes (allows dynamic content)
 * - When closing: height is first locked to current value, then after a frame
 *   (via requestAnimationFrame) transitions to 0px for smooth animation
 *
 * Nested Collapsers:
 * Supports nesting - each collapser only claims its direct toggle/target elements,
 * not those belonging to nested child collapsers.
 *
 * Dataset Config Options:
 * - open: boolean - Initial open state (default: false)
 * - collapsable: boolean - Whether collapsing is enabled (default: true)
 * - toggle_selector: string - Custom selector for toggle elements
 * - target_selector: string - Custom selector for target element
 * - apply_to_class: string - Additional class(es) to apply open/closed states to
 * - accordion: boolean - Enable accordion behavior (default: false)
 *
 * Accordion Behavior:
 * When enabled (via `accordion: true` config or `collapser--accordion` class),
 * opening this collapser will close any sibling collapsers that also have
 * accordion enabled. Siblings are collapsers with the same parent element.
 *
 * CSS Warning:
 * Do NOT use `display: none` to hide closed targets in CSS. The component needs
 * to measure content height for smooth animations - `display: none` prevents this.
 * Instead use `overflow: hidden` (the component already sets height: 0 when closed).
 *
 * Test Page: /crew/test/components/collapser
 */
export const CollapserConfig = {
    componentTypeKey: 'Collapser',
    baseHtmlClass: 'collapser',
    selector: '.collapser',
    datasetConfigKey: 'collapser',
};

export class Collapser extends Component {

    static get config() {
        return CollapserConfig;
    }

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: Collapser.config
        });
        this.setDatasetConfig(Collapser.config.datasetConfigKey);

        this.applyToClass = this.getDatasetConfigValue('apply_to_class');
        this.isOpen = this.getDatasetConfigValue('open') ?? false;
        this.isCollapsable = this.getDatasetConfigValue('collapsable') ?? true;

        this.toggles = this.getToggles();
        this.target = this.getTarget();

        this.render();
    }

    render() {
        this.conditionallySetHtmlClassState(this.isCollapsable, 'collapsable');
        this.swapHtmlClassStates(this.isOpen, 'open', 'closed');
        if (!!this.target) this.target.render();
        this.toggles.forEach((toggle) => toggle.render());
        if (this.applyToClass) {
            this.applyToClass.split(' ').forEach((className) => {
                this.conditionallySetHtmlClassState(this.isCollapsable, 'collapsable', {htmlClass: className});
                this.swapHtmlClassStates(this.isOpen, 'open', 'closed', {htmlClass: className});
            });
        }
    }

    toggleOpen(toggle) {
        const wasOpen = this.isOpen;

        // If opening in accordion mode, close siblings first then open
        if (!wasOpen && this.isAccordion()) {
            const openSiblings = this.getAccordionSiblings().filter(s => s.isOpen && s.target);

            if (openSiblings.length > 0) {
                // Close siblings first
                openSiblings.forEach(sibling => sibling.target.toggleToClosed());

                // Then open this one after siblings finish closing
                const transitionDuration = this.target ? this.target.transitionDuration : 300;
                setTimeout(() => {
                    if (this.target) {
                        this.target.toggleToOpen();
                        this.render();
                    } else {
                        this.isOpen = true;
                        this.render();
                    }
                }, transitionDuration);
                return;
            }
        }

        // Normal toggle (closing, or opening with no siblings to close)
        if (this.target) {
            this.target.toggleCollapse();
        } else {
            this.isOpen = !this.isOpen;
        }
    }

    getToggles() {
        const selector = this.getDatasetConfigValue('toggle_selector') || CollapserToggle.config.selector;
        const toggles = [];
        const toggleElements = this.element.querySelectorAll(selector);
        toggleElements.forEach((toggleElement) => {
            // Only claim toggles whose closest collapser ancestor is this collapser
            if (toggleElement.closest(Collapser.config.selector) === this.element) {
                toggles.push(new CollapserToggle(toggleElement, this));
            }
        });
        return toggles;
    }

    getTarget() {
        const selector = this.getDatasetConfigValue('target_selector') || CollapserTarget.config.selector;
        const targetElements = this.element.querySelectorAll(selector);
        for (const targetElement of targetElements) {
            // Only claim targets whose closest collapser ancestor is this collapser
            if (targetElement.closest(Collapser.config.selector) === this.element) {
                return new CollapserTarget(targetElement, this);
            }
        }
        return null;
    }

    // Accordion behavior

    isAccordion() {
        const fromConfig = this.getDatasetConfigValue('accordion');
        const fromClass = this.getStatesFromHtmlClasses().includes('accordion');
        return fromConfig || fromClass;
    }

    getAccordionSiblings() {
        const parent = this.element.parentElement;
        if (!parent) return [];

        const siblingElements = parent.querySelectorAll(`:scope > ${Collapser.config.selector}`);
        const siblings = [];

        siblingElements.forEach((el) => {
            if (el === this.element) return; // Exclude self
            const collapser = getElementComponentOfType(el, Collapser.config.componentTypeKey);
            if (collapser && collapser.isAccordion()) {
                siblings.push(collapser);
            }
        });

        return siblings;
    }

    closeAccordionSiblings() {
        const siblings = this.getAccordionSiblings();
        siblings.forEach((sibling) => {
            if (sibling.isOpen && sibling.target) {
                sibling.target.toggleToClosed();
            }
        });
    }

}



